import React, {
  createContext,
  ElementRef,
  ReactElement,
  useContext,
  useRef,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  FormStep,
  SignUpFormContextPropsType,
  SignUpFormContextType,
  SignUpFormData,
  UseSignUpFormActionsType,
  UseSignUpFormStateType,
} from "../../SignUpFormTypes/signUpFormTypes";
import { getFieldQuestions } from "./signUpHelpers";
import ReCAPTCHA from "react-google-recaptcha";

const SignUpFormContext = createContext<
  | (SignUpFormContextType & UseSignUpFormStateType & UseSignUpFormActionsType)
  | null
>(null);

export const SignUpFormProvider: React.FC<
  {
    children: ReactElement;
    steps: FormStep[];
    useSignUpFormStateMethods: UseSignUpFormStateType &
      UseSignUpFormActionsType;
  } & SignUpFormContextPropsType
> = ({
  children,
  useSignUpFormStateMethods,
  useSpecialties,
  steps,
  ...props
}) => {
  const reCaptchaRef = useRef<ElementRef<typeof ReCAPTCHA>>(null);

  const { specialties } = useSpecialties(0);
  const methods = useForm<SignUpFormData>({
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      agreement: false,
      questions: getFieldQuestions(steps),
      phone: "",
      profession: "",
      otherProfession: "",
      offer: 1,
      promoCode: "",
    },
    mode: "all",
  });

  return (
    <FormProvider {...methods}>
      <SignUpFormContext.Provider
        value={{
          ...props,
          ...useSignUpFormStateMethods,
          specialties,
          useSpecialties,
          reCaptchaRef,
        }}
      >
        {process.env.GATSBY_ENVIRONMENT !== "dev" && (
          <ReCAPTCHA
            sitekey={process.env.GATSBY_GRECAPTCHA_SITE_KEY!}
            ref={reCaptchaRef}
            size="invisible"
          />
        )}
        {children}
      </SignUpFormContext.Provider>
    </FormProvider>
  );
};

export const useSignUpForm = () => {
  const context = useContext(SignUpFormContext);

  if (!context)
    throw new Error("useSignUpForm must be used within a SignUpFormProvider");

  return context;
};
